/**
 * BankImport — téléversement d'un relevé (OFX / QFX / CSV), aperçu, mapping des colonnes CSV si nécessaire, import.
 */
import { useRef, useState } from "react";
import { FileUp, Loader2, Upload } from "lucide-react";
import { api } from "@/api";
import { toast } from "@/lib/toast";
import { Btn, Field, errMsg, money } from "../../comptabilite/_shared";

const FIELDS = [["date", "Date *"], ["description", "Description"], ["amount", "Montant signé"], ["debit", "Retrait / débit"], ["credit", "Dépôt / crédit"], ["balance", "Solde"], ["reference", "Référence / n° chèque"]];
const DATE_FMTS = [["", "Auto"], ["%Y-%m-%d", "AAAA-MM-JJ"], ["%Y/%m/%d", "AAAA/MM/JJ"], ["%d/%m/%Y", "JJ/MM/AAAA"], ["%m/%d/%Y", "MM/JJ/AAAA"], ["%d-%m-%Y", "JJ-MM-AAAA"], ["%Y%m%d", "AAAAMMJJ"]];
const sel = "border border-slate-300 rounded-lg px-2 py-1.5 text-xs bg-white w-full";

export default function BankImport({ base, accountId, bankAccounts, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [opts, setOpts] = useState({ mapping: {}, has_header: "", date_format: "", invert_sign: false });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const form = (f, o) => {
    const fd = new FormData();
    fd.append("file", f);
    if (Object.keys(o.mapping).length) fd.append("mapping", JSON.stringify(o.mapping));
    if (o.has_header) fd.append("has_header", o.has_header);
    if (o.date_format) fd.append("date_format", o.date_format);
    if (o.invert_sign) fd.append("invert_sign", "true");
    return fd;
  };

  const analyse = async (f, o = opts) => {
    setBusy(true); setResult(null);
    try {
      const r = await api.post(`${base}/bank/preview`, form(f, o), { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(r.data);
      if (r.data.format === "csv" && !Object.keys(o.mapping).length) setOpts((p) => ({ ...p, mapping: r.data.info.mapping || {} }));
    } catch (e) { toast.error(errMsg(e, "Fichier illisible")); setPreview(null); }
    finally { setBusy(false); }
  };

  const onFile = (f) => { if (!f) return; setFile(f); setOpts({ mapping: {}, has_header: "", date_format: "", invert_sign: false }); analyse(f, { mapping: {}, has_header: "", date_format: "", invert_sign: false }); };
  const setMap = (field, idx) => { const m = { ...opts.mapping }; if (idx === "") delete m[field]; else m[field] = Number(idx); const o = { ...opts, mapping: m }; setOpts(o); analyse(file, o); };
  const setOpt = (k, v) => { const o = { ...opts, [k]: v }; setOpts(o); analyse(file, o); };

  const doImport = async () => {
    if (!file || !accountId) return;
    setBusy(true);
    try {
      const fd = form(file, opts); fd.append("account_id", accountId);
      const r = await api.post(`${base}/bank/import`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(r.data);
      if (!r.data.imported) toast.info(`Aucune nouvelle ligne — ${r.data.duplicates} doublon(s) ignoré(s)`);
      else { toast.success(`${r.data.imported} ligne(s) importée(s) · ${r.data.auto_matched} conciliée(s) automatiquement`); onImported?.(); }
    } catch (e) { toast.error(errMsg(e, "Import impossible")); }
    finally { setBusy(false); }
  };

  const acc = bankAccounts.find((a) => a.id === accountId);
  const info = preview?.info || {};
  const needsMapping = preview?.format === "csv" && info.needs_mapping;
  const ncols = info.ncols || 0;

  return (
    <div className="space-y-4" data-testid="bank-import">
      <div onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); onFile(e.dataTransfer.files?.[0]); }}
        className="border-2 border-dashed border-teal-200 bg-teal-50/40 rounded-xl p-6 text-center cursor-pointer hover:bg-teal-50" onClick={() => fileRef.current?.click()} data-testid="bank-dropzone">
        <input ref={fileRef} type="file" accept=".ofx,.qfx,.qbo,.csv,.txt" className="hidden" onChange={(e) => onFile(e.target.files?.[0])} data-testid="bank-file-input" />
        <FileUp className="w-8 h-8 mx-auto text-teal-500 mb-2" />
        <div className="text-sm font-semibold text-slate-800">{file ? file.name : "Déposez le relevé ici ou cliquez pour choisir"}</div>
        <div className="text-xs text-slate-500 mt-1">OFX · QFX · CSV (AccèsD Desjardins reconnu automatiquement, autres banques avec mapping des colonnes) — compte : <strong>{acc ? `${acc.number} — ${acc.name}` : "—"}</strong></div>
      </div>

      {busy && !preview && <div className="text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>}

      {preview && (
        <div className="bg-white border rounded-xl p-4 space-y-3" data-testid="bank-preview">
          <div className="flex items-center gap-3 flex-wrap text-xs text-slate-600">
            <span className="font-semibold text-slate-800">Format : {preview.format === "ofx" ? "OFX / QFX" : info.preset === "desjardins" ? "CSV Desjardins AccèsD" : "CSV"}</span>
            {preview.count > 0 && <span data-testid="bank-preview-count">{preview.count} ligne(s) · du {preview.date_from} au {preview.date_to}</span>}
            {preview.count > 0 && <span>Dépôts <strong className="font-mono text-emerald-700">{money(preview.total_in)}</strong> · Retraits <strong className="font-mono text-rose-700">{money(preview.total_out)}</strong></span>}
            {info.closing_balance != null && <span>Solde de clôture <strong className="font-mono">{money(info.closing_balance)}</strong>{info.balance_date ? ` au ${info.balance_date}` : ""}</span>}
            {info.skipped > 0 && <span className="text-amber-700">{info.skipped} ligne(s) ignorée(s) (date ou montant illisible)</span>}
          </div>

          {preview.format === "csv" && (
            <div className="space-y-2" data-testid="bank-csv-mapping">
              {needsMapping && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">Colonnes non reconnues — indiquez au moins la Date et le Montant (ou Retrait / Dépôt).</p>}
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                {FIELDS.map(([f, label]) => (
                  <Field key={f} label={label}>
                    <select value={opts.mapping[f] ?? ""} onChange={(e) => setMap(f, e.target.value)} className={sel} data-testid={`bank-map-${f}`}>
                      <option value="">—</option>
                      {Array.from({ length: ncols }, (_, i) => <option key={i} value={i}>{info.header?.[i] ? `${i + 1}. ${info.header[i]}` : `Colonne ${i + 1}`}</option>)}
                    </select>
                  </Field>
                ))}
              </div>
              <div className="flex flex-wrap gap-3 items-end">
                <Field label="Format de date"><select value={opts.date_format} onChange={(e) => setOpt("date_format", e.target.value)} className={sel} data-testid="bank-date-format">{DATE_FMTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></Field>
                <Field label="Première ligne"><select value={opts.has_header} onChange={(e) => setOpt("has_header", e.target.value)} className={sel} data-testid="bank-has-header"><option value="">Détection auto</option><option value="true">En-têtes</option><option value="false">Données</option></select></Field>
                <label className="inline-flex items-center gap-2 text-xs text-slate-700 pb-2"><input type="checkbox" checked={opts.invert_sign} onChange={(e) => setOpt("invert_sign", e.target.checked)} className="w-4 h-4 accent-teal-600" data-testid="bank-invert-sign" /> Inverser le signe (relevé de carte de crédit)</label>
              </div>
              {needsMapping && info.sample?.length > 0 && (
                <div className="overflow-x-auto"><table className="text-[11px] border"><tbody>{info.sample.slice(0, 4).map((r, i) => <tr key={i} className="divide-x">{r.map((c, j) => <td key={j} className="px-2 py-1 whitespace-nowrap text-slate-600">{c}</td>)}</tr>)}</tbody></table></div>
              )}
            </div>
          )}

          {preview.rows?.length > 0 && (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-xs" data-testid="bank-preview-table">
                <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500"><tr><th className="text-left px-2 py-1.5">Date</th><th className="text-left px-2 py-1.5">Description</th><th className="text-left px-2 py-1.5">Réf.</th><th className="text-right px-2 py-1.5">Montant</th><th className="text-right px-2 py-1.5">Solde</th></tr></thead>
                <tbody className="divide-y">{preview.rows.map((r, i) => (
                  <tr key={i}><td className="px-2 py-1 whitespace-nowrap">{r.date}</td><td className="px-2 py-1 truncate max-w-[320px]">{r.description}{r.memo ? <span className="text-slate-400"> · {r.memo}</span> : ""}</td><td className="px-2 py-1">{r.ref}</td>
                    <td className={`px-2 py-1 text-right font-mono ${r.amount < 0 ? "text-rose-700" : "text-emerald-700"}`}>{money(r.amount)}</td><td className="px-2 py-1 text-right font-mono text-slate-500">{r.balance == null ? "" : money(r.balance)}</td></tr>
                ))}</tbody>
              </table>
              {preview.count > preview.rows.length && <div className="text-[11px] text-slate-400 px-2 py-1">… et {preview.count - preview.rows.length} autre(s) ligne(s)</div>}
            </div>
          )}

          <div className="flex justify-end gap-2">
            <Btn tone="emerald" icon={Upload} onClick={doImport} busy={busy} disabled={!preview.count || needsMapping || !accountId} testId="bank-import-btn">Importer {preview.count} ligne(s) dans {acc?.number}</Btn>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-900" data-testid="bank-import-result">
          <strong>{result.imported}</strong> ligne(s) importée(s) · <strong>{result.duplicates}</strong> doublon(s) ignoré(s) · <strong>{result.auto_matched}</strong> conciliée(s) automatiquement
          {result.rules_posted > 0 && <> · <strong>{result.rules_posted}</strong> comptabilisée(s) par règle</>}{result.rules_suggested > 0 && <> · <strong>{result.rules_suggested}</strong> suggestion(s) de compte</>}
        </div>
      )}
    </div>
  );
}
